import { Box, Tooltip, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { formatDurationSeconds } from '../timeEntries';
import { colors } from '../../../core/theme/theme';
import type { EntryPosition } from './calendarLayout';

export interface CalendarEntryBlockProps {
  id: string;
  position: EntryPosition;
  projectName: string;
  designationName: string;
  description: string;
  durationSeconds: number;
  color: string;
  isTeammate: boolean;
  selected?: boolean;
  teammateDisplayName?: string;
  readOnlyTooltip?: string;
  onClick: (e: React.MouseEvent) => void;
  onDoubleClick?: (e: React.MouseEvent) => void;
  onPointerDown: (e: React.PointerEvent) => void;
}

const MIN_HEIGHT_TWO_LINES = 32;
const MIN_HEIGHT_THREE_LINES = 64;
const MIN_HEIGHT_FOUR_LINES = 96;

export default function CalendarEntryBlock({
  id,
  position,
  projectName,
  designationName,
  description,
  durationSeconds,
  color,
  isTeammate,
  selected = false,
  readOnlyTooltip,
  onClick,
  onDoubleClick,
  onPointerDown,
}: CalendarEntryBlockProps) {
  const isInteractive = !isTeammate;
  const height = position.height;

  const block = (
    <Box
      data-testid={`calendar-entry-${id}`}
      data-entry-id={id}
      onClick={isInteractive ? onClick : undefined}
      onDoubleClick={isInteractive ? onDoubleClick : undefined}
      onPointerDown={isInteractive ? onPointerDown : undefined}
      sx={{
        position: 'absolute',
        top: position.top,
        height: position.height,
        left: `${position.left * 100}%`,
        width: `${position.width * 100}%`,
        bgcolor: alpha(color, 0.15),
        borderLeft: `3px ${isTeammate ? 'dashed' : 'solid'} ${color}`,
        borderRadius: '4px',
        outline: selected ? `2px solid ${colors.kronosAccent}` : 'none',
        outlineOffset: selected ? '-1px' : undefined,
        cursor: isInteractive ? 'pointer' : 'default',
        opacity: isTeammate ? 0.7 : 1,
        overflow: 'hidden',
        px: 0.5,
        py: 0.25,
        boxSizing: 'border-box',
        userSelect: 'none',
        '&:hover': isInteractive
          ? { bgcolor: alpha(color, 0.25) }
          : undefined,
      }}
    >
      {/* Project name — always shown */}
      <Typography
        variant="caption"
        sx={{
          fontWeight: 600,
          lineHeight: 1.2,
          display: 'block',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          color: 'text.primary',
        }}
      >
        {projectName}
      </Typography>

      {/* Designation — shown when tall enough */}
      {height >= MIN_HEIGHT_TWO_LINES && (
        <Typography
          variant="caption"
          sx={{
            lineHeight: 1.2,
            display: 'block',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            color: 'text.secondary',
          }}
        >
          {designationName}
        </Typography>
      )}

      {/* Duration — shown when taller */}
      {height >= MIN_HEIGHT_THREE_LINES && (
        <Typography
          variant="caption"
          sx={{
            lineHeight: 1.2,
            display: 'block',
            color: 'text.secondary',
          }}
        >
          {formatDurationSeconds(durationSeconds)}
        </Typography>
      )}

      {/* Description — shown when tallest */}
      {height >= MIN_HEIGHT_FOUR_LINES && description && (
        <Typography
          variant="caption"
          sx={{
            lineHeight: 1.2,
            display: 'block',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            color: 'text.secondary',
            mt: 0.25,
          }}
        >
          {description}
        </Typography>
      )}

      {/* Resize handles — top and bottom */}
      {isInteractive && (
        <>
          <Box
            data-resize="start"
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: 6,
              cursor: 'ns-resize',
            }}
          />
          <Box
            data-resize="end"
            sx={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              height: 6,
              cursor: 'ns-resize',
            }}
          />
        </>
      )}
    </Box>
  );

  if (readOnlyTooltip) {
    return <Tooltip title={readOnlyTooltip}>{block}</Tooltip>;
  }
  return block;
}
